import React from 'react';
import { useDrop } from 'react-dnd';
import classNameBind from 'classnames/bind';
import styles from './TransferSegment.module.css';
import ListContainer from '../ListContainer';
import Header from '../../../Header';
import Checkbox from '../../../Checkbox';
import Information from '../../../Information';
import Button from '../../../Button';
import Stack from '../../../Stack';
import Icon from '../../../Icon';
import List from '../../../List';
import { TextStyle } from '../../../TextStyle';
import { TextField } from '../../../TextField';
import { Pagination } from '../../../Pagination';
import Label from '../../../Label';

const bindClasses = classNameBind.bind(styles);

const FailedState = ({ t }) => (
  <Stack
    vertical
    horizontalAlignment="middle"
    spacing="extraNarrow"
  >
    <Stack.Item>
      <Icon
        color="red"
        name="exclamation-mark-circle"
        size="default"
      />
    </Stack.Item>
    <Stack.Item>
      <TextStyle type="negative">
        {t('transfer.from-segment.load.failed.description')}
      </TextStyle>
    </Stack.Item>
  </Stack>
);

const renderSortingButton = el => (
  el.onClick ? (
    <List.SortableHeader
      order={el.sortByOrder}
      text={el.name}
      selected={Boolean(el.sortByOrder)}
      onClick={el.onClick}
    />
  ) : (
    <div className={bindClasses('columnLabel')}><Label type="small">{el.name}</Label></div>
  )
);

const TransferSegment = ({
  t,
  title,
  customElements,
  selected,
  search,
  listHeaders,
  list,
  pagination,
  limit,
  isFrom,
  errors,
  onItemSelect,
  onLimitChange,
  onSelectAll,
  deselectAllButtonMode,
  isLoading,
  onDragEnd,
  failed,
}) => {
  const [{ canDrop, isOver, item }, drop] = useDrop(() => ({
    accept: isFrom ? 'to' : 'from',
    drop: () => ({ name: isFrom ? 'from' : 'to' }),
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      item: monitor.getItem(),
    }),
  }));

  const checkboxAllLabel = selected.hideTotal ? `${selected.count} ${t('Transfer.selected')}` : `${selected.count}/${selected.total} ${t('Transfer.selected')}`;

  return (
    <div className={bindClasses('wrapper')} data-ppuc-id={`Transfer.Segment.${isFrom ? 'From' : 'To'}`}>
      <div className={bindClasses('header')}>
        <Header>{title}</Header>
      </div>
      <div className={bindClasses('container')}>
        <div className={bindClasses('selected-section')} data-ppuc-id="selected-section">
          <div className={bindClasses('selected')}>
            <div className={bindClasses('checkbox')}>
              <Checkbox
                disabled={selected.checkbox.disabled}
                checked={selected.checkbox.checked}
                onChange={selected.checkbox.onChange}
                checkedMixed={selected.checkbox.checkedMixed}
                name="select-all"
                id={`select-all-${isFrom ? 'from' : 'to'}`}
                dataId="Transfer.TransferSegment.SelectAllCheckbox"
                label={checkboxAllLabel}
              />
            </div>
            {onSelectAll && (
              <div className={bindClasses('select-all-button')}>
                <Button
                  appearance="simple"
                  text={t(deselectAllButtonMode ? 'Transfer.deselectAllButton' : 'Transfer.selectAllButton')}
                  onClick={onSelectAll}
                  noHover
                  minimalWidth
                />
              </div>
            )}
          </div>
          { customElements }
        </div>
        {errors && errors.length ? (
          <div className={bindClasses('errors')}>
            { errors.map((el, index) => (
              <Information type={el.type} key={index}>{el.content}</Information>
            ))}
          </div>
        ) : null}
        <div className={bindClasses('search')}>
          <TextField
            name="search"
            type="text"
            icon="search"
            dataId="Transfer.TransferSegment.Search"
            placeholder={t('Transfer.searchPlaceholder')}
            clearable
            value={search.value}
            onChange={search.onChange}
          />
        </div>
        <div className={bindClasses('sort')}>
          {renderSortingButton(listHeaders.nameHeader)}
          {renderSortingButton(listHeaders.labelHeader)}
        </div>
        <div
          ref={drop}
          className={bindClasses('list', { centered: isLoading || failed, droppable: canDrop })}
          style={{ minHeight: `${limit * 50}px` }}
          data-ppuc-id="list-section"
        >
          {failed ? (
            <div className={bindClasses('failed-wrapper')}>
              <FailedState t={t} />
            </div>
          ) : (
            <ListContainer
              {...{ isLoading, canDrop, isOver, list, onItemSelect, isFrom, onDragEnd }}
              selected={{ count: selected.count }}
              item={item ? { isFrom: item.isFrom } : null}
            />
          )}
        </div>
      </div>
      <div className={bindClasses('pagination')}>
        <Pagination
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          onPageChange={pagination.onPageChange}
          limitOptions={pagination.limitOptions}
          total={pagination.total}
          forcePrevDisabled={pagination.forcePrevDisabled}
          forceNextDisabled={pagination.forceNextDisabled}
          onLimitChange={onLimitChange}
          limit={limit}
          showTotal={false}
          framed={false}
        />
      </div>
    </div>
  );
};

TransferSegment.defaultProps = {
  customElements: () => null,
  listHeaders: { nameHeader: {}, labelHeader: {} },
  pagination: {},
};

export default TransferSegment;
